import React from 'react';
import { cn } from '@/lib/utils';
import { LayoutProps } from '@/types/types';

const GAP_MAP: Record<string, string> = {
  none: 'gap-0',
  '2': 'gap-0.5',
  '4': 'gap-1',
  '8': 'gap-2',
  '12': 'gap-3',
  '16': 'gap-4',
  '20': 'gap-5',
  '24': 'gap-6',
  '32': 'gap-8',
  '36': 'gap-9',
  '44': 'gap-11',
  '48': 'gap-12',
};

const ALIGN_MAP: Record<string, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
};

export const Header: React.FC<LayoutProps> = ({ className = '', children }) => {
  return (
    <div
      className={cn(
        'flex justify-between items-center w-full',
        '[&_h1]:m-0 [&_h1]:text-foreground [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:leading-normal',
        '[&_h2]:m-0 [&_h2]:text-foreground [&_h2]:text-base [&_h2]:font-semibold [&_h2]:leading-6',
        '[&_label]:text-sm [&_label]:font-bold [&_label]:leading-normal [&_label]:text-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
};

type Props = {
  gapSize?: 'none' | '2' | '4' | '8' | '12' | '16' | '20' | '24' | '32' | '36' | '44' | '48';
  align?: 'center' | 'start' | 'end';
  justify?:
    | 'justify-center'
    | 'justify-start'
    | 'justify-end'
    | 'justify-between'
    | 'justify-around';
  fullWidth?: boolean;
  fullHeight?: boolean;
  inlineRow?: boolean;
  noShrink?: boolean;
};
type CombinedProps = LayoutProps & Props;

export const TitleAndComment: React.FC<CombinedProps> = ({
  children,
  gapSize = '4',
  className,
}) => {
  return (
    <div className={cn('flex flex-col', GAP_MAP[gapSize] ?? 'gap-1', className)}>{children}</div>
  );
};

export const Row: React.FC<CombinedProps> = ({
  children,
  gapSize = '4',
  align = 'center',
  fullWidth = true,
  fullHeight = false,
  justify = 'justify-start',
  inlineRow = false,
  noShrink = false,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex items-center',
        ALIGN_MAP[align] ?? 'items-center',
        justify,
        GAP_MAP[gapSize] ?? 'gap-1',
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        gapSize === '44' && 'overflow-hidden',
        inlineRow &&
          '[&>span]:block [&>span]:w-[90%] [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap',
        noShrink && 'shrink-0',
        className,
      )}
    >
      {children}
    </div>
  );
};

export const Column: React.FC<CombinedProps> = ({
  children,
  gapSize = '4',
  fullWidth = true,
  fullHeight = false,
  align = 'start',
  justify = 'justify-start',
  inlineRow = false,
  noShrink = false,
  className,
}) => {
  return (
    <div
      className={cn(
        'flex flex-col',
        ALIGN_MAP[align] ?? 'items-start',
        justify,
        GAP_MAP[gapSize] ?? 'gap-1',
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        gapSize === '44' && 'overflow-hidden',
        inlineRow &&
          '[&>span]:block [&>span]:w-[90%] [&>span]:overflow-hidden [&>span]:text-ellipsis [&>span]:whitespace-nowrap',
        noShrink && 'shrink-0',
        className,
      )}
    >
      {children}
    </div>
  );
};
